import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Inbox, CalendarCheck, CheckCircle2, Building2, ArrowRight } from "lucide-react";

const COLORS = { new: "#0D9488", contacted: "#1E3A8A", scheduled: "#f59e0b", closed: "#10B981" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "Total Leads", value: stats.total_leads, icon: Inbox, color: "text-primary bg-primary/10" },
    { label: "New Leads", value: stats.new_leads, icon: Inbox, color: "text-secondary bg-secondary/10" },
    { label: "Scheduled", value: stats.scheduled, icon: CalendarCheck, color: "text-amber-600 bg-amber-100" },
    { label: "Closed", value: stats.closed, icon: CheckCircle2, color: "text-accent bg-accent/10" },
    { label: "Clinics", value: stats.clinics, icon: Building2, color: "text-secondary bg-secondary/10" },
  ] : [];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
      <p className="text-muted-foreground mt-1">Your lead pipeline at a glance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 rounded-2xl" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></div>
            <p className="font-heading font-extrabold text-3xl mt-3">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6 rounded-2xl">
          <h3 className="font-heading font-bold mb-4">Leads by status</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.by_status || []}>
                <XAxis dataKey="status" tickLine={false} axisLine={false} tick={{ fontSize: 12, textTransform: "capitalize" }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {(stats?.by_status || []).map((e) => <Cell key={e.status} fill={COLORS[e.status] || "#0D9488"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold">Recent leads</h3>
            <Link to="/admin/leads" className="text-sm text-primary flex items-center gap-1" data-testid="dashboard-view-leads">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="space-y-2">
            {recent.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No leads yet.</p>}
            {recent.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <div>
                  <p className="font-medium text-sm">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.procedure || "General"} · {l.type}</p>
                </div>
                <span className="text-xs rounded-full px-2.5 py-1 capitalize" style={{ background: `${COLORS[l.status]}1a`, color: COLORS[l.status] }}>{l.status}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
